import React, { useMemo } from 'react';
import { Select, Tooltip, Button, Dropdown } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  HomeOutlined,
  ShopOutlined,
  DollarOutlined,
  CarOutlined,
  FundOutlined,
  ImportOutlined,
  BarChartOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../../contexts/AuthContext';
import { useRoute } from '../../../contexts/RouteContext';
import { isDealerMember, isOrgAdmin, canAccessUsersMenu, isLHPHMember } from '../../../utils';
import { User } from '../../../types/User.type';
import { DealerCompanyFlags, anyFlagOn, dealerImports } from '../../../utils/dealerCompanyFlags';
import './SectionSelector.scss';

interface Section {
  name: string;
  path: string;
  icon: React.ReactNode;
  defaultSubpage?: string;
  dealerDefaultSubpage?: string | ((flags: DealerCompanyFlags | null) => string);
}

interface SectionSelectorProps {
  collapsed?: boolean;
  onNavigate?: () => void;
}

// lhphcapital-kdm6: a dealer's sections follow its own company's flags, as legacy's menu does.
const ALL_SECTIONS: (Section & {
  visibleTo: (user: User, flags: DealerCompanyFlags | null) => boolean;
})[] = [
  {
    name: 'Home',
    path: '/home',
    icon: <HomeOutlined />,
    visibleTo: () => true,
  },
  {
    name: 'Dealers',
    path: '/dealers',
    icon: <ShopOutlined />,
    visibleTo: user => !isDealerMember(user), // not in legacy's dealer menu (42bw)
  },
  {
    name: 'Loans',
    path: '/loans',
    icon: <DollarOutlined />,
    defaultSubpage: '/dashboard',
    visibleTo: () => true,
  },
  {
    name: 'Floorplans',
    path: '/floorplans',
    icon: <CarOutlined />,
    defaultSubpage: '/dashboard',
    visibleTo: (user, flags) => !isDealerMember(user) || anyFlagOn(flags, ['FloorplanON']),
  },
  {
    name: 'Borrowing Base',
    path: '/borrowing-base',
    icon: <FundOutlined />,
    defaultSubpage: '/dashboard',
    visibleTo: (user, flags) => !isDealerMember(user) || anyFlagOn(flags, ['BorrowBase']),
  },
  {
    name: 'Imports',
    path: '/imports',
    icon: <ImportOutlined />,
    defaultSubpage: '/unsold-inventory',
    // The dealer's first import its company has turned on; none on, the section's own page.
    dealerDefaultSubpage: flags => {
      const first = dealerImports(flags)[0];
      return first ? `/${first.subpage}` : '';
    },
    visibleTo: () => true,
  },
  {
    name: 'Reports',
    path: '/reports',
    icon: <BarChartOutlined />,
    visibleTo: () => true, // dealers get their own list (3xb8); /reports redirects them
  },
  {
    name: 'Admin',
    path: '/admin',
    icon: <SettingOutlined />,
    visibleTo: user => isOrgAdmin(user) || canAccessUsersMenu(user) || isLHPHMember(user),
  },
];

// Sections that use a company ID in the URL
const COMPANY_SECTIONS = new Set([
  '/dealers',
  '/loans',
  '/floorplans',
  '/borrowing-base',
  '/imports',
]);

const SectionSelector: React.FC<SectionSelectorProps> = ({ collapsed, onNavigate }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { dealerFlags } = useRoute();

  const availableSections = useMemo(() => {
    if (!user) return [];
    return ALL_SECTIONS.filter(s => s.visibleTo(user, dealerFlags));
  }, [user, dealerFlags]);

  // Detect current section and company ID from URL
  const { currentSection, currentCompanyId } = useMemo(() => {
    const pathname = location.pathname;
    const match = pathname.match(
      /^\/(home|dealers|loans|floorplans|borrowing-base|imports|reports|admin)(?:\/(\d+))?/
    );
    return {
      currentSection: match ? `/${match[1]}` : '',
      currentCompanyId: match?.[2] || '',
    };
  }, [location.pathname]);

  const currentSectionObj = availableSections.find(s => s.path === currentSection);

  const handleSectionChange = (sectionPath: string) => {
    const section = availableSections.find(s => s.path === sectionPath);
    if (!section) return;

    let targetPath = section.path;

    // If we have a company ID and the target section supports it, carry it over
    if (currentCompanyId && COMPANY_SECTIONS.has(section.path)) {
      const dealerSubpage =
        typeof section.dealerDefaultSubpage === 'function'
          ? section.dealerDefaultSubpage(dealerFlags)
          : section.dealerDefaultSubpage;
      const subpage =
        isDealerMember(user) && section.dealerDefaultSubpage !== undefined
          ? dealerSubpage || ''
          : section.defaultSubpage || '';
      targetPath = `${section.path}/${currentCompanyId}${subpage}`;
    }

    navigate(targetPath);
    onNavigate?.();
  };

  if (!availableSections.length) return null;

  // Collapsed sidebar: show icon button with dropdown
  if (collapsed) {
    const dropdownItems = availableSections.map(section => ({
      key: section.path,
      icon: section.icon,
      label: section.name,
      onClick: () => handleSectionChange(section.path),
    }));

    return (
      <div className="section-selector-collapsed">
        <Dropdown
          menu={{ items: dropdownItems, selectedKeys: [currentSection] }}
          trigger={['click']}
        >
          <Tooltip title={currentSectionObj?.name || 'Switch Section'} placement="right">
            <Button type="text" size="small" className="section-selector-icon-btn">
              {currentSectionObj?.icon || <HomeOutlined />}
            </Button>
          </Tooltip>
        </Dropdown>
      </div>
    );
  }

  // Expanded sidebar: show Select dropdown
  return (
    <div className="section-selector">
      <Select
        style={{ width: '100%' }}
        value={currentSection || undefined}
        onChange={handleSectionChange}
        placeholder="Switch Section"
        popupMatchSelectWidth={false}
      >
        {availableSections.map(section => (
          <Select.Option key={section.path} value={section.path}>
            <span className="section-option">
              <span className="section-option-icon">{section.icon}</span>
              {section.name}
            </span>
          </Select.Option>
        ))}
      </Select>
    </div>
  );
};

export default SectionSelector;
